export type CityMapCenter = {
  lat: number
  lng: number
  zoom: number
}

export const indiaDefaultCenter: CityMapCenter = { lat: 22.5937, lng: 78.9629, zoom: 5 }

const titleCaseCity = (value: string) =>
  value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

export const normalizeIndiaCityName = (value?: string | null) => {
  const raw = String(value || '').trim()
  if (!raw) return ''

  const compact = raw.replace(/\s+/g, ' ')
  const key = compact.toLowerCase()
  const aliases: Record<string, string> = {
    bangalore: 'Bengaluru',
    bengaluru: 'Bengaluru',
    mysore: 'Mysuru',
    mysuru: 'Mysuru',
    gurgaon: 'Gurugram',
    gurugram: 'Gurugram',
    'greater noida': 'Noida',
    'gautam buddha nagar': 'Noida',
    'gautam buddh nagar': 'Noida',
    gbn: 'Noida',
    belgaum: 'Belagavi',
    belgavi: 'Belagavi',
    belagavi: 'Belagavi',
    davangere: 'Davanagere',
    devangere: 'Davanagere',
    davanagere: 'Davanagere',
    mangaluru: 'Mangalore',
    mangalore: 'Mangalore',
    puducherry: 'Pondicherry',
    pondicherry: 'Pondicherry',
    'chenna i': 'Chennai',
    kanchipuram: 'ROTN',
    kanchipursm: 'ROTN',
    kanicpursm: 'ROTN',
    kancheepuram: 'ROTN',
    vellore: 'ROTN',
    ranipet: 'ROTN',
    tirupattur: 'ROTN',
    tirupathur: 'ROTN',
    vaniyambadi: 'ROTN',
    ambur: 'ROTN',
    hosur: 'ROTN',
    chengalpattu: 'ROTN',
    singaperumal: 'ROTN',
    'singaperumal koil': 'ROTN',
    potheri: 'ROTN',
    guduvancheri: 'ROTN',
    rotn: 'ROTN',
    rok: 'ROK',
    roi: 'ROI',
    rots: 'ROTS',
  }

  return aliases[key] || (compact === compact.toUpperCase() && compact.length <= 5 ? compact : titleCaseCity(compact))
}

export const cityMapCenters: Record<string, CityMapCenter> = {
  [normalizeIndiaCityName('All Cities')]: indiaDefaultCenter,
  Bengaluru: { lat: 12.9716, lng: 77.5946, zoom: 11 },
  Chennai: { lat: 13.0827, lng: 80.2707, zoom: 11 },
  Hyderabad: { lat: 17.385, lng: 78.4867, zoom: 11 },
  Delhi: { lat: 28.6139, lng: 77.209, zoom: 10 },
  Mumbai: { lat: 19.076, lng: 72.8777, zoom: 11 },
  Noida: { lat: 28.5355, lng: 77.391, zoom: 11 },
  Mysuru: { lat: 12.2958, lng: 76.6394, zoom: 11 },
  Coimbatore: { lat: 11.0168, lng: 76.9558, zoom: 11 },
  ROTN: { lat: 11.1271, lng: 78.6569, zoom: 7 },
  Kerala: { lat: 10.8505, lng: 76.2711, zoom: 7 },
  Ghaziabad: { lat: 28.6692, lng: 77.4538, zoom: 11 },
  ROK: { lat: 14.5204, lng: 75.7224, zoom: 7 },
  Belagavi: { lat: 15.8497, lng: 74.4977, zoom: 11 },
  Warangal: { lat: 17.9689, lng: 79.5941, zoom: 11 },
  Khammam: { lat: 17.2473, lng: 80.1514, zoom: 11 },
  Davanagere: { lat: 14.4644, lng: 75.9218, zoom: 11 },
  Hosur: { lat: 12.7409, lng: 77.8253, zoom: 11 },
  Karimnagar: { lat: 18.4386, lng: 79.1288, zoom: 11 },
  Pondicherry: { lat: 11.9416, lng: 79.8083, zoom: 11 },
  Faridabad: { lat: 28.4089, lng: 77.3178, zoom: 11 },
  Gorakhpur: { lat: 26.7606, lng: 83.3732, zoom: 11 },
  Gujarat: { lat: 22.2587, lng: 71.1924, zoom: 7 },
  Guntur: { lat: 16.3067, lng: 80.4365, zoom: 11 },
  Gurugram: { lat: 28.4595, lng: 77.0266, zoom: 11 },
  Hubballi: { lat: 15.3647, lng: 75.124, zoom: 11 },
  Jabalpur: { lat: 23.1815, lng: 79.9864, zoom: 11 },
  Kanchipuram: { lat: 12.8342, lng: 79.7036, zoom: 11 },
  Chengalpattu: { lat: 12.6819, lng: 79.9888, zoom: 12 },
  Kochi: { lat: 9.9312, lng: 76.2673, zoom: 11 },
  Mangalore: { lat: 12.9141, lng: 74.856, zoom: 11 },
  Nellore: { lat: 14.4426, lng: 79.9865, zoom: 11 },
  Nizamabad: { lat: 18.6725, lng: 78.0941, zoom: 11 },
  Pune: { lat: 18.5204, lng: 73.8567, zoom: 11 },
  ROI: indiaDefaultCenter,
  Varanasi: { lat: 25.3176, lng: 82.9739, zoom: 11 },
  Vellore: { lat: 12.9165, lng: 79.1325, zoom: 11 },
  Ambur: { lat: 12.7904, lng: 78.7166, zoom: 12 },
  Vaniyambadi: { lat: 12.6816, lng: 78.6212, zoom: 12 },
  Tirupathur: { lat: 12.495, lng: 78.5678, zoom: 12 },
  Vijayawada: { lat: 16.5062, lng: 80.648, zoom: 11 },
  Mandya: { lat: 12.5218, lng: 76.8951, zoom: 11 },
  Hassan: { lat: 13.0072, lng: 76.0962, zoom: 11 },
  Tumakuru: { lat: 13.3409, lng: 77.101, zoom: 11 },
  Kolkata: { lat: 22.5726, lng: 88.3639, zoom: 11 },
  Ahmedabad: { lat: 23.0225, lng: 72.5714, zoom: 11 },
  Jaipur: { lat: 26.9124, lng: 75.7873, zoom: 11 },
  Lucknow: { lat: 26.8467, lng: 80.9462, zoom: 11 },
  Surat: { lat: 21.1702, lng: 72.8311, zoom: 11 },
  Bhopal: { lat: 23.2599, lng: 77.4126, zoom: 11 },
  Indore: { lat: 22.7196, lng: 75.8577, zoom: 11 },
  Nagpur: { lat: 21.1458, lng: 79.0882, zoom: 11 },
  Chandigarh: { lat: 30.7333, lng: 76.7794, zoom: 11 },
  Goa: { lat: 15.2993, lng: 74.124, zoom: 8 },
}

export const getCityMapCenter = (city?: string | null, centers = cityMapCenters) => {
  const normalized = normalizeIndiaCityName(city)
  return normalized ? centers[normalized] : undefined
}

export const getNearestMappedCity = (lat: number, lng: number, centers = cityMapCenters) => {
  let minDist = Infinity
  let nearest = ''
  const rolledUpCenters = new Set([
    'Ambur',
    'Chengalpattu',
    'Hosur',
    'Kanchipuram',
    'Tirupathur',
    'Vaniyambadi',
    'Vellore',
  ])

  Object.entries(centers).forEach(([city, pos]) => {
    if (city === 'All Cities' || city === 'ROI' || rolledUpCenters.has(city)) return
    const d = Math.sqrt(Math.pow(pos.lat - lat, 2) + Math.pow(pos.lng - lng, 2))
    if (d < minDist) {
      minDist = d
      nearest = city
    }
  })

  return nearest || 'Unknown'
}

const isWithin = (
  lat: number,
  lng: number,
  bounds: { north: number; south: number; east: number; west: number },
) => lat <= bounds.north && lat >= bounds.south && lng <= bounds.east && lng >= bounds.west

const isInsidePolygon = (lat: number, lng: number, polygon: Array<[number, number]>) => {
  let inside = false

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [latI, lngI] = polygon[i]
    const [latJ, lngJ] = polygon[j]
    const intersects = ((lngI > lng) !== (lngJ > lng))
      && (lat < ((latJ - latI) * (lng - lngI)) / (lngJ - lngI) + latI)

    if (intersects) inside = !inside
  }

  return inside
}

export const getOperationalCityForCoordinates = (lat: number, lng: number) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return ''

  const noidaPolygon: Array<[number, number]> = [
    [28.62, 77.30],
    [28.64, 77.39],
    [28.56, 77.52],
    [28.42, 77.60],
    [28.34, 77.48],
    [28.42, 77.32],
  ]

  const greaterNoidaPolygon: Array<[number, number]> = [
    [28.50, 77.40],
    [28.58, 77.56],
    [28.46, 77.68],
    [28.28, 77.62],
    [28.32, 77.42],
  ]

  if (isInsidePolygon(lat, lng, noidaPolygon) || isInsidePolygon(lat, lng, greaterNoidaPolygon)) {
    return 'Noida'
  }

  if (isWithin(lat, lng, { north: 28.78, south: 28.60, east: 77.62, west: 77.34 })) {
    return 'Ghaziabad'
  }

  // Keep Delhi tight so Noida/Greater Noida points do not get swallowed by the NCR fallback.
  if (isWithin(lat, lng, { north: 28.78, south: 28.46, east: 77.34, west: 76.82 })) {
    return 'Delhi'
  }

  // South Bengaluru border belt: Bommasandra, Jigani, Anekal and Attibele are Bengaluru ops,
  // not Hosur, even though Hosur is geographically close.
  if (isWithin(lat, lng, { north: 12.96, south: 12.70, east: 77.82, west: 77.48 })) {
    return 'Bengaluru'
  }

  if (isWithin(lat, lng, { north: 13.18, south: 12.75, east: 77.85, west: 77.35 })) {
    return 'Bengaluru'
  }

  // Dedicated Tamil Nadu city buckets are only Chennai and Coimbatore; other TN points roll into ROTN.
  if (isWithin(lat, lng, { north: 13.35, south: 12.75, east: 80.45, west: 79.85 })) {
    return 'Chennai'
  }

  if (isWithin(lat, lng, { north: 11.35, south: 10.75, east: 77.25, west: 76.65 })) {
    return 'Coimbatore'
  }

  // Hosur is Tamil Nadu, but operationally it should not appear as its own city card.
  if (isWithin(lat, lng, { north: 12.78, south: 12.66, east: 77.92, west: 77.82 })) {
    return 'ROTN'
  }

  if (isWithin(lat, lng, { north: 12.87, south: 12.72, east: 78.82, west: 78.62 })) {
    return 'ROTN'
  }

  if (isWithin(lat, lng, { north: 12.76, south: 12.58, east: 78.72, west: 78.48 })) {
    return 'ROTN'
  }

  if (isWithin(lat, lng, { north: 12.58, south: 12.38, east: 78.68, west: 78.42 })) {
    return 'ROTN'
  }

  // GST Road / Singaperumal Koil / Potheri belt rolls into ROTN.
  if (isWithin(lat, lng, { north: 12.88, south: 12.58, east: 80.14, west: 79.82 })) {
    return 'ROTN'
  }

  // Broad Tamil Nadu fallback outside Chennai and Coimbatore.
  if (isWithin(lat, lng, { north: 13.65, south: 8.0, east: 80.35, west: 77.35 })) {
    return 'ROTN'
  }

  return ''
}
